import React from "react";
import moment from "moment";
import { UsagePlan } from "pages/subscription/SubscriptionStyled";
import { FaRegHardDrive } from "react-icons/fa6";
import AccountId from "components/atoms/text/labels/AccountId";
import { convertBytesToTB, formatNumber } from "utils/functions";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { UnderlineText } from "components/atoms/text/Text";
import type { UsageStorageResult } from "api/interfaces/planInterface.interface";
import { CloudModalAccount } from "pages/subscription/modal/addCloud/AddCloudModal";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { getAccountTimeFormatDate } from "utils/timeUtil";

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const format2Digits = (value: number) => {
  return formatNumber(value, { fractionDigits: 2 });
};

type Props = {
  onAddCloudStorage: (row: CloudModalAccount) => void;
  storageAlloctaionData: UsageStorageResult | null;
};

export default function StorageAllocationPanel(props: Props) {
  const totalCredits = props.storageAlloctaionData?.usage.amount ?? 0;
  const usedCredits = props.storageAlloctaionData?.usage.used ?? 0;
  const activatedEndUsers = props.storageAlloctaionData?.storages?.length ?? 0;
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  return (
    <UsagePlan.GraphWrapper className="storage-allocation-panel">
      <div className="storage-panel-header">
        <div className="header-left">
          <div className="header-icon">
            <FaRegHardDrive size={18} />
          </div>
          <div className="header-text">
            <h3>Current Storage Allocation</h3>
            <p>
              Storage is used across <b>{formatNumber(activatedEndUsers)}</b>{" "}
              end users
            </p>
          </div>
        </div>
        <div className="header-right">
          <div className="header-right-value">
            {format2Digits(usedCredits)} / {format2Digits(totalCredits)}{" "}
            TB-months
          </div>
        </div>
      </div>

      {props.storageAlloctaionData !== null ? (
        <>
          <div className="storage-credit-usage">
            <div className="credit-usage-title">
              <span>Credit Usage</span>
            </div>
            <div className="credit-usage-bar">
              {(() => {
                const used = usedCredits;
                const total = totalCredits;
                const pct =
                  total > 0 ? clampNumber((used / total) * 100, 0, 100) : 0;
                return (
                  <>
                    <div className="credit-usage-track">
                      <div
                        className="credit-usage-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="credit-usage-percent">
                      {Math.floor(pct)}%
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="storage-allocation-list">
            {props.storageAlloctaionData.storages.map((row) => {
              const cloudModalProps: CloudModalAccount = {
                ...row,
                usedStorage: row.cloudStorage?.usedStorage ?? 0,
                totalStorage: row.cloudStorage?.totalStorage ?? 0,
              };

              const usedTb = convertBytesToTB(
                row.cloudStorage?.usedStorage ?? 0,
                true
              );
              const totalTb = convertBytesToTB(
                row.cloudStorage?.totalStorage ?? 0,
                true
              );

              const planTb = row.planCloudStorage ?? 0;

              return (
                <div className="allocation-row" key={row.accountId}>
                  <div className="allocation-left">
                    <div className="allocation-org">
                      <AccountId
                        accountId={row.accountNumber}
                        level={LEVEL_TYPE.EU}
                      />
                      <b>{row.name}</b>{" "}
                    </div>
                    {row.planActiveDate && (
                      <div className="allocation-since">
                        since{" "}
                        {getAccountTimeFormatDate(
                          row.planActiveDate,
                          selectedAccount
                        )}
                      </div>
                    )}
                  </div>
                  <div className="allocation-right">
                    <RBACWrapper requiredPermissions="manageBillingAndLicenses">
                      <UnderlineText
                        fontSize={13}
                        style={{ display: "inline" }}
                        onClick={() => props.onAddCloudStorage(cloudModalProps)}
                      >
                        Add Cloud Storage
                      </UnderlineText>
                    </RBACWrapper>

                    <div className="allocation-metric">
                      <div className="allocation-value">
                        <b>
                          {format2Digits(usedTb)} / {format2Digits(totalTb)} TB
                        </b>
                      </div>
                      <div className="allocation-sub">
                        {`${format2Digits(planTb)} TB`}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="storage-empty-note">
          No storage allocated yet. Assign storage to end users from the
          Channels
          {">"} End Users tab.
        </div>
      )}
    </UsagePlan.GraphWrapper>
  );
}
